import { useCallback, useEffect, useMemo, useState } from 'react';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { BrowserRouter } from "react-router-dom";
import {
  ClientContext,
  GraphQLClient,
} from 'graphql-hooks';
import i18n from "i18next";

import { resources, initReactI18next } from './i18n/config';
import { AuthProvider } from './context/auth';
import { NavigationRoot } from './screens/NavigationRoot';

const FONTS = [
  '400 14px Poppins', '600 14px Poppins', '700 14px Poppins', '700 28px Rajdhani',
  // Home only (join us title).
  '400 14px Anton', '400 14px "Gasoek One"',
];
const FONTS_TIMEOUT = 1000;

function App() {
  const [appLoaded, setAppLoaded] = useState(false);
  const getLang = useCallback(() => {
    const lang = window.navigator.language;
    if (lang.split('-').length) {
      const tmp = lang.split('-')[0];
      if (tmp === 'fr' || tmp === 'en') {
        return tmp;
      } else {
        return 'en';
      }
    }
    if (lang && (lang === 'fr' || lang === 'en')) {
      return window.navigator.language;
    } else {
      return 'en';
    }
  }, []);
  const getUrl = useCallback(() => {
    // In development the server runs on the same machine as this page: its host follows the network
    // (localhost on the Mac, its IP from a phone), never a fixed IP that changes from home to the office.
    let url = `http://${window.location.hostname}:3000/graphql`;
    if (
      process.env.REACT_APP_ENV === 'production'
      && (
        window.location.hostname === 'soskills.io'
        || window.location.hostname === 'localhost'
        || window.location.hostname.includes('192.168.1')
      )
    ) {
      url = 'https://backend.soskills.io/graphql';
    } else if (
      process.env.REACT_APP_ENV === 'production'
      && window.location.hostname === 'soskills.fr'
    ) {
      url = 'https://backend.soskills.fr/graphql';
    }
    return url;
  }, [process.env.REACT_APP_ENV]);

  // No cache: admin screens must always show fresh data after a mutation.
  const client = useMemo(() => new GraphQLClient({
    url: getUrl(),
  }), [getUrl]);
  const lang = getLang();
  const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const appHeight = () => {
    const doc = document.documentElement;
    doc.style.setProperty('--app-height', `${window.innerHeight}px`)
  }

  window.addEventListener('resize', appHeight)

  useEffect(() => {
    appHeight();
    i18n
      //.use(LanguageDetector)
      .use(initReactI18next)
      .init({
        resources,
        // React already escapes what it renders: without this, "l'arme" shows "l&#39;arme".
        interpolation: {
          escapeValue: false,
        },
        react: {
          useSuspense: false,
        }
      }, async () => {
        i18n.changeLanguage(lang);
        // The first render waits for the fonts (1s at most): no text in another font or invisible.
        await Promise.race([
          Promise.all(FONTS.map((font) => document.fonts.load(font))).catch(() => undefined),
          new Promise((resolve) => setTimeout(resolve, FONTS_TIMEOUT)),
        ]);
        setAppLoaded(true);
        //i18n.changeLanguage('fr');
      });
  }, [lang]);

  if (!appLoaded) {
    return null;
  }

  return (
    <GoogleOAuthProvider clientId={process.env.REACT_APP_GOOGLE_WEBCLIENT_ID!}>
      <BrowserRouter>
        <ClientContext.Provider value={client}>
          <AuthProvider
            lang={lang}
            tz={tz}
          >
            <NavigationRoot />
          </AuthProvider>
        </ClientContext.Provider>
      </BrowserRouter>
    </GoogleOAuthProvider>
  );
}

export default App;
